/**
 * Voice Conversation Persistence
 *
 * Saves voice session transcripts to Firestore using the same
 * conversation/messages schema as text chat.
 */

import { collection, doc, addDoc, writeBatch, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import type { TranscriptEntry } from '../hooks/useGeminiLive';

export async function saveVoiceTranscript(
  conversationId: string | undefined,
  transcript: TranscriptEntry[]
): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  if (transcript.length === 0) throw new Error('No transcript to save');

  let convId = conversationId;

  // Create new conversation if needed
  if (!convId) {
    const convRef = await addDoc(collection(db, 'conversations'), {
      userId: user.uid,
      title: `Voice Check-In ${new Date().toLocaleDateString()}`,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      messageCount: transcript.length,
    });
    convId = convRef.id;
  }

  // Batch-write all transcript entries as messages
  const batch = writeBatch(db);
  const messagesCol = collection(db, 'conversations', convId, 'messages');

  for (const entry of transcript) {
    const msgRef = doc(messagesCol);
    batch.set(msgRef, {
      role: entry.role,
      content: entry.text,
      source: 'voice',
      createdAt: Timestamp.fromDate(new Date(entry.timestamp)),
    });
  }

  // Update conversation metadata
  const convDocRef = doc(db, 'conversations', convId);
  batch.update(convDocRef, {
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
  return convId;
}
